import React, { useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { QueryApiContext } from '../../../contexts/QueryContext';
import { formatDistanceToNow } from 'date-fns';
import { Button } from '../../../components/ui';
import EditableTitle from '../../../components/editableTitle/EditableTitle';
import PageHeading from '../../../components/pageHeading/PageHeading';
import PaginatedTable, { type ColumnDef } from '../../../components/paginatedTable/PaginatedTable';
import { usePagination } from '../../../hooks/usePagination';
import { formatDateForDisplay } from '../../../utils/utils';

type ChatConversation = {
  id: string;
  title: string | null;
  userId: string;
  createdBy: string;
  createdAt: Date;
  updatedBy: string;
  updatedAt: Date;
};

const ChatHistoryPage: React.FC = () => {
  const navigate = useNavigate();
  const restQueryClient = useContext(QueryApiContext);
  const queryClient = useQueryClient();
  const { queryParams, setPage, handleSort, sortBy, sortOrder } = usePagination<'title' | 'createdAt' | 'updatedAt'>({
    defaultSortBy: 'updatedAt',
    defaultSortOrder: 'desc'
  });

  const { data, isLoading, isError } = restQueryClient.chatConversations.list.useQuery(
    ['chatConversations', queryParams],
    { query: queryParams }
  );

  const updateConversationMutation = restQueryClient.chatConversations.update.useMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chatConversations'] });
    }
  });

  const handleTitleSave = async (id: string, title: string) => {
    await updateConversationMutation.mutateAsync({
      params: { id },
      body: { title }
    });
  };

  const columns: ColumnDef<ChatConversation>[] = useMemo(
    () => [
      {
        key: 'title',
        header: 'Title',
        sortable: true,
        render: (c) => (
          <EditableTitle
            value={c.title || ''}
            placeholder="Untitled Chat"
            onSave={(next) => handleTitleSave(c.id, next)}
            ariaLabel={`Edit title for chat ${c.title || 'Untitled Chat'}`}
            editTriggerOnly
          />
        )
      },
      {
        key: 'updatedAt',
        header: 'Last Updated',
        sortable: true,
        render: (c) => (
          <span title={formatDateForDisplay(c.updatedAt)}>
            {formatDistanceToNow(new Date(c.updatedAt), { addSuffix: true })}
          </span>
        )
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const conversations = (data?.body?.data || []) as ChatConversation[];
  const pagination = data?.body?.pagination;

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 w-full mt-10">
      <PageHeading heading="Chat History">
        <Button color="blue" className="mr-0" type="button" onClick={() => navigate('/chat')}>
          Start a chat
        </Button>
      </PageHeading>
      <div className="flex mt-4">
        <div className="w-full">
          {isError ? (
            <p>Error loading chat history.</p>
          ) : (
            <PaginatedTable
              columns={columns}
              data={conversations}
              pagination={pagination}
              isLoading={isLoading}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={handleSort}
              onPageChange={setPage}
              onRowClick={(c) => navigate(`/chat/${c.id}`)}
              getRowKey={(c) => c.id}
              emptyMessage="No chat history found. Start your first conversation to see it here."
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatHistoryPage;
